// FiscalDocsTab — "Mes achats → Reçus fiscaux" : documents FNE/RNE (sandbox)
// émis pour les commandes du client par le AYA Fiscal Engine.
import { useEffect, useState } from "react";

const API = process.env.REACT_APP_BACKEND_URL;

const TYPE_STYLE = {
  FNE: { bg: "rgba(59,130,246,0.14)", color: "#2563EB", label: "FNE" },
  RNE: { bg: "rgba(16,185,129,0.14)", color: "#059669", label: "RNE" },
  CREDIT_NOTE: { bg: "rgba(239,68,68,0.12)", color: "#DC2626", label: "AVOIR" },
};

export default function FiscalDocsTab({ phone, isAuthenticated }) {
  const [docs, setDocs] = useState(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    if (!phone) { setDocs([]); return; }
    fetch(`${API}/api/v3/fiscal/documents?customerId=${encodeURIComponent(phone)}`)
      .then((r) => r.json())
      .then((b) => setDocs(b.documents || []))
      .catch(() => { setError(true); setDocs([]); });
  }, [phone]);

  if (!isAuthenticated) {
    return (
      <div className="mc-empty" data-testid="fiscal-docs-anon">
        <div className="mc-empty-icon">🧾</div>
        <div className="mc-empty-title">Reçus fiscaux</div>
        <div className="mc-empty-sub">Connecte-toi pour retrouver les reçus (RNE) et factures (FNE) de tes commandes.</div>
      </div>
    );
  }
  if (docs === null) return <div style={{ padding: 20, opacity: 0.6, fontSize: 13 }}>⏳ Chargement…</div>;
  if (error) {
    return (
      <div className="mc-empty" data-testid="fiscal-docs-error">
        <div className="mc-empty-icon">⚠️</div>
        <div className="mc-empty-title">Impossible de charger tes reçus</div>
        <div className="mc-empty-sub">Vérifie ta connexion puis réessaie.</div>
      </div>
    );
  }
  if (docs.length === 0) {
    return (
      <div className="mc-empty" data-testid="fiscal-docs-empty">
        <div className="mc-empty-icon">🧾</div>
        <div className="mc-empty-title">Aucun reçu fiscal pour l&apos;instant</div>
        <div className="mc-empty-sub">Chaque commande payée génère automatiquement son document fiscal (mode sandbox).</div>
      </div>
    );
  }
  return (
    <div data-testid="fiscal-docs-list" style={{ display: "flex", flexDirection: "column", gap: 10 }}>
      {docs.map((d) => {
        const t = TYPE_STYLE[d.type] || { bg: "rgba(100,116,139,0.12)", color: "#475569", label: d.type };
        return (
          <div key={d.documentId} data-testid={`fiscal-doc-${d.documentId}`} style={{
            padding: 12, borderRadius: 12, border: "1px solid var(--civ-border, rgba(0,0,0,0.10))",
            background: "var(--civ-card, rgba(255,255,255,0.03))",
          }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <span style={{ fontSize: 10.5, fontWeight: 800, padding: "3px 9px", borderRadius: 999, background: t.bg, color: t.color }}>{t.label}</span>
              <span style={{ fontSize: 12.5, fontWeight: 700, flex: 1, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{d.number}</span>
              {d.mode === "SANDBOX" && (
                <span style={{ fontSize: 9.5, padding: "2px 7px", borderRadius: 999, background: "rgba(245,158,11,0.16)", color: "#B45309", fontWeight: 800 }}>SANDBOX</span>
              )}
              <a
                href={`${API}/api/v3/fiscal/documents/${d.documentId}/pdf`}
                target="_blank"
                rel="noreferrer"
                data-testid={`fiscal-doc-pdf-${d.documentId}`}
                style={{ fontSize: 10.5, fontWeight: 800, padding: "3px 10px", borderRadius: 999, background: "rgba(16,185,129,0.14)", color: "#059669", textDecoration: "none", whiteSpace: "nowrap" }}
              >📄 PDF</a>
            </div>
            <div style={{ display: "flex", gap: 12, marginTop: 7, fontSize: 12, opacity: 0.85, flexWrap: "wrap" }}>
              <span style={{ fontWeight: 800 }}>{Number(d.totalAmount || 0).toLocaleString("fr-FR")} {d.currency || "XOF"}</span>
              {Number.isFinite(d.taxAmount) && d.taxAmount > 0 && <span>dont TVA {Number(d.taxAmount).toLocaleString("fr-FR")}</span>}
              <span style={{ opacity: 0.65 }}>{(d.createdAt || "").slice(0, 10)}</span>
            </div>
            {Array.isArray(d.items) && d.items.length > 0 && (
              <div style={{ fontSize: 11.5, opacity: 0.65, marginTop: 5 }}>
                {d.items.slice(0, 3).map((i) => `${i.name}×${i.qty}`).join(", ")}{d.items.length > 3 ? "…" : ""}
              </div>
            )}
            <code style={{ display: "block", fontSize: 9.5, opacity: 0.45, marginTop: 5, fontFamily: "ui-monospace, Menlo, monospace" }}>
              Réf. certification : {d.certificationReference || d.number} · Commande {d.transactionId}
            </code>
          </div>
        );
      })}
    </div>
  );
}
